import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api';
import { ModalForm } from '@/components/ui';
import styles from './styles.module.scss';

interface TournamentHolderCoefficientFormProps {
  match: any;
  tournament?: any;
  onClose: () => void;
  onSuccess: () => void;
}

const TournamentHolderCoefficientForm: React.FC<TournamentHolderCoefficientFormProps> = ({
  match,
  tournament,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation(['tournament', 'common']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // If coefficients are not set (source is 'default'), show empty fields
  // so user can enter values directly without deleting defaults
  const coefficientsSet = match.coefficients_set === true;

  const [formData, setFormData] = useState({
    team1_win_coefficient: coefficientsSet ? (match.team1_win_coefficient || '') : '',
    team2_win_coefficient: coefficientsSet ? (match.team2_win_coefficient || '') : '',
    draw_coefficient: coefficientsSet ? (match.draw_coefficient || '') : ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const coefficients = {
        team1_win_coefficient: parseFloat(formData.team1_win_coefficient.toString()) || 1.01,
        team2_win_coefficient: parseFloat(formData.team2_win_coefficient.toString()) || 1.01,
        draw_coefficient: parseFloat(formData.draw_coefficient.toString()) || 1.01
      };

      await apiClient.updateMatchCoefficients(tournament?.id, match.id.toString(), coefficients);
      onSuccess();
    } catch (err: any) {
      setError(err.message || t('tournament:errorSavingCoefficients'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalForm
      title={t('tournament:tournamentCoefficients')}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={t('common:save')}
      isSubmitting={isSubmitting}
      error={error}
    >
      {/* Match info */}
      <div className={styles.matchInfo}>
        <h3 className={styles.matchTeams}>
          {(match as any).team1_short || match.team1} vs {(match as any).team2_short || match.team2}
        </h3>
        <p className={styles.matchHint}>
          {t('tournament:setCoefficients')}
        </p>
      </div>

      {/* Team 1 win coefficient */}
      <div className="d-flex flex-column g-4">
        <label className={styles.fieldLabel}>
          {t('tournament:team1WinCoeff', { team: match.team1 })}
        </label>
        <input
          type="number"
          step="0.01"
          min="1.01"
          value={formData.team1_win_coefficient}
          onChange={(e) => setFormData(prev => ({ ...prev, team1_win_coefficient: e.target.value }))}
          className="field"
          placeholder="1.50"
          required
        />
      </div>

      {/* Draw coefficient */}
      <div className="d-flex flex-column g-4">
        <label className={styles.fieldLabel}>
          {t('tournament:drawCoeff')}
        </label>
        <input
          type="number"
          step="0.01"
          min="1.01"
          value={formData.draw_coefficient}
          onChange={(e) => setFormData(prev => ({ ...prev, draw_coefficient: e.target.value }))}
          className="field"
          placeholder="3.50"
          required
        />
      </div>

      {/* Team 2 win coefficient */}
      <div className="d-flex flex-column g-4">
        <label className={styles.fieldLabel}>
          {t('tournament:team1WinCoeff', { team: match.team2 })}
        </label>
        <input
          type="number"
          step="0.01"
          min="1.01"
          value={formData.team2_win_coefficient}
          onChange={(e) => setFormData(prev => ({ ...prev, team2_win_coefficient: e.target.value }))}
          className="field"
          placeholder="2.80"
          required
        />
      </div>
    </ModalForm>
  );
};

export default TournamentHolderCoefficientForm;
